import type {
  AtoSession,
  CurrentUser,
  ListRunsResult,
  RunDetail,
  SettingsView,
  SuperSearchStatus,
  UpdateSettings,
} from './types';

/**
 * Thin fetch wrapper. Calls use relative paths so the same bundle works behind the .NET host
 * in production and through Vite's proxy in dev.
 *
 * Auth is an ASP.NET Identity cookie — `credentials: 'include'` on every call, with nothing
 * kept in browser storage. A 401 surfaces as `UnauthorizedError` so the shell can show the
 * sign-in screen.
 */

export class UnauthorizedError extends Error {
  constructor() {
    super('Sign-in required.');
    this.name = 'UnauthorizedError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (response.status === 401) throw new UnauthorizedError();

  if (!response.ok) {
    const body = await response.text();
    throw new Error(readError(body) || `${response.status} ${response.statusText}`);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Identity replies with ProblemDetails and an `errors` bag; surface something actionable. */
function readError(body: string): string | null {
  if (!body) return null;
  try {
    const parsed = JSON.parse(body) as {
      detail?: string;
      title?: string;
      errors?: Record<string, string[] | string>;
    };
    if (parsed.errors) {
      const messages = Object.values(parsed.errors).flat();
      if (messages.length) return messages.join(' ');
    }
    return parsed.detail ?? parsed.title ?? null;
  } catch {
    return body;
  }
}

export interface ListRunsParams {
  status?: SuperSearchStatus;
  needsAttention?: boolean;
  search?: string;
  take?: number;
}

export const api = {
  // useCookies=true makes Identity issue the auth cookie instead of a bearer token.
  login(email: string, password: string) {
    return request<void>('/api/v1/users/login?useCookies=true', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  logout() {
    return request<void>('/api/v1/users/logout', { method: 'POST', body: '{}' });
  },

  me() {
    return request<CurrentUser>('/api/v1/users/me');
  },

  listRuns({ status, needsAttention, search, take = 100 }: ListRunsParams = {}) {
    const query = new URLSearchParams();
    if (status) query.set('status', status);
    if (needsAttention) query.set('needsAttention', 'true');
    if (search) query.set('search', search);
    query.set('take', String(take));
    return request<ListRunsResult>(`/api/v1/runs?${query}`);
  },

  getRun(id: number) {
    return request<RunDetail>(`/api/v1/runs/${id}`);
  },

  retryRun(id: number) {
    return request<void>(`/api/v1/runs/${id}/retry`, { method: 'POST' });
  },

  createRun(email: string, taxYear?: number) {
    return request<{ runId: number }>('/api/v1/runs', {
      method: 'POST',
      body: JSON.stringify({ email, taxYear: taxYear ?? null, contactId: null }),
    });
  },

  atoSession() {
    return request<AtoSession>('/api/v1/ato/session');
  },

  connectAto() {
    return request<void>('/api/v1/ato/connect', { method: 'POST' });
  },

  getSettings() {
    return request<SettingsView>('/api/v1/settings');
  },

  updateSettings(update: UpdateSettings) {
    return request<SettingsView>('/api/v1/settings', {
      method: 'PUT',
      body: JSON.stringify(update),
    });
  },
};
